/**
 * Ticket KI1, Nebenbefund 2: Dauerhaft gültige Bildadressen für Teilen-Angaben.
 *
 * Signierte Adressen laufen ab (Personenfotos nach einem Tag) – Teilen-Dienste
 * und Assistenten lesen die Angaben aber beliebig später. Die Antwort ist eine
 * eigene, unveränderliche Adresse, die beim Abruf serverseitig ein frisch
 * signiertes Bild ausliefert (Route unter `/api/public/bild`). Die Ablage
 * selbst bleibt geschützt, nichts wird neu hochgeladen.
 *
 * Der Ablagepfad steht verschlüsselt (base64url) in der Adresse – er ist keine
 * lesbare Angabe und nicht erratbar. Bewusst reine Logik, damit die Prüfungen
 * ohne Datenbank laufen. Die Anzeige im Browser nutzt weiterhin die kurz
 * signierten Adressen; diese Adressform dient ausschließlich den
 * Teilen-Metainformationen.
 */

export type PermanentImageArt = "mentoring" | "person" | "blog" | "wahlkalender";

export const PERMANENT_IMAGE_ARTS: readonly PermanentImageArt[] = [
  "mentoring",
  "person",
  "blog",
  "wahlkalender",
] as const;

/** Signaturdauer der Weiterleitung: kurz, der Abruf holt sich frisches Bild. */
export const PERMANENT_IMAGE_TTL_SECONDS = 60 * 60;

export const PERMANENT_IMAGE_BASE = "/api/public/bild";

export function encodePermanentImageId(path: string): string {
  const bytes = new TextEncoder().encode(path);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/gu, "-").replace(/\//gu, "_").replace(/=+$/gu, "");
}

/** Rückweg der Route: ohne gültige Rückübersetzung kein Bild. */
export function decodePermanentImageId(id: string): string | null {
  if (!id) return null;
  try {
    const base64 = id.replace(/-/gu, "+").replace(/_/gu, "/").padEnd(Math.ceil(id.length / 4) * 4, "=");
    const binary = atob(base64);
    const decoded = new TextDecoder().decode(Uint8Array.from(binary, (char) => char.charCodeAt(0)));
    if (!decoded || encodePermanentImageId(decoded) !== id) return null;
    if (decoded.startsWith("/") || decoded.includes("..") || decoded.includes("\\")) return null;
    return decoded;
  } catch {
    return null;
  }
}

/** Adresspfad innerhalb dieser Website. */
export function permanentImagePath(art: PermanentImageArt, path: string): string {
  return `${PERMANENT_IMAGE_BASE}/${art}/${encodePermanentImageId(path)}`;
}

/** Vollständige Adresse für Teilen-Angaben. */
export function permanentImageUrl(art: PermanentImageArt, path: string): string {
  return `https://creategy.de${permanentImagePath(art, path)}`;
}

/** Gibt nur den Pfad einer eigenen, dauerhaft ausgelieferten Bildadresse zurück. */
export function pathFromPermanentImageUrl(art: PermanentImageArt, url: string): string | null {
  try {
    const parsed = new URL(url, "https://creategy.de");
    if (parsed.origin !== "https://creategy.de") return null;
    const prefix = `${PERMANENT_IMAGE_BASE}/${art}/`;
    if (!parsed.pathname.startsWith(prefix)) return null;
    return decodePermanentImageId(parsed.pathname.slice(prefix.length));
  } catch {
    return null;
  }
}
